import React, { useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import * as Actions from '../redux/actions';
import ReportDraft from './report_components/ReportDraft';
import '../styles/report.scss';

const ReportDrafts = (props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('updated');
  // Admins can switch between their own drafts and every employee's.
  const [scope, setScope] = useState('mine');
  const canSeeAll = Boolean(props.user?.admin && !props.user?.operator);

  useEffect(() => {
    props.fetchReportDrafts(null, props.authToken, null, { scope: scope === 'all' ? 'all' : undefined });
  }, [props.authToken, scope]);

  const filteredDrafts = useMemo(() => {
    const drafts = props.drafts || [];
    const query = searchTerm.trim().toLowerCase();
    const matched = !query
      ? drafts
      : drafts.filter((draft) =>
          (draft.title || '').toLowerCase().includes(query) ||
          (draft.author || '').toLowerCase().includes(query)
        );

    const ts = (draft) => (
      sortBy === 'created'
        ? (draft.created_at || 0)
        : (draft.updated_at || 0)
    );

    return [...matched].sort((a, b) => ts(b) - ts(a));
  }, [props.drafts, searchTerm, sortBy]);

  const totalCount = props.drafts?.length || 0;
  const showEmpty = filteredDrafts.length === 0;

  return (
    <section className="report-drafts" aria-label="Draft reports">
      <div className="report-drafts-toolbar">
        <div className="report-drafts-heading">
          <span className="report-drafts-label">Open drafts</span>
          {totalCount > 0 && (
            <span className="report-drafts-count">{totalCount}</span>
          )}
        </div>

        <div className="report-drafts-search">
          <SearchIcon className="report-drafts-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="browser-default"
            placeholder="Filter drafts…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Filter drafts"
          />
        </div>

        <div className="report-drafts-sort-wrap">
          <span className="report-drafts-sort-label" id="report-drafts-sort-label">
            Order drafts by:
          </span>
          <div className="report-drafts-sort" role="group" aria-labelledby="report-drafts-sort-label">
            <button
              type="button"
              className={sortBy === 'updated' ? 'is-selected' : ''}
              aria-pressed={sortBy === 'updated'}
              onClick={() => setSortBy('updated')}
            >
              Last updated
            </button>
            <button
              type="button"
              className={sortBy === 'created' ? 'is-selected' : ''}
              aria-pressed={sortBy === 'created'}
              onClick={() => setSortBy('created')}
            >
              Created
            </button>
          </div>
        </div>

        {canSeeAll && (
          <div className="report-drafts-sort-wrap">
            <span className="report-drafts-sort-label" id="report-drafts-scope-label">
              Show:
            </span>
            <div className="report-drafts-sort" role="group" aria-labelledby="report-drafts-scope-label">
              <button
                type="button"
                className={scope === 'mine' ? 'is-selected' : ''}
                aria-pressed={scope === 'mine'}
                onClick={() => setScope('mine')}
              >
                Mine
              </button>
              <button
                type="button"
                className={scope === 'all' ? 'is-selected' : ''}
                aria-pressed={scope === 'all'}
                onClick={() => setScope('all')}
              >
                All employees
              </button>
            </div>
          </div>
        )}
      </div>

      {showEmpty ? (
        <div className="report-drafts-empty">
          <EditIcon className="report-drafts-empty-icon" aria-hidden="true" />
          <p className="report-drafts-empty-title">
            {totalCount === 0 ? 'No open drafts' : 'No matching drafts'}
          </p>
          <p className="report-drafts-empty-copy">
            {totalCount > 0
              ? 'Try a different name or author.'
              : scope === 'all'
                ? 'No employee has an open draft.'
                : 'Start a new service report to save a draft here.'}
          </p>
        </div>
      ) : (
        <ul className="report-drafts-list">
          {filteredDrafts.map((draft) => (
            <li key={draft.id}>
              <ReportDraft draft={draft} timeField={sortBy} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

function mapStateToProps(state) {
  return {
    drafts: state.drafts,
    authToken: state.authToken,
    user: state.user,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportDrafts);
